/**
 * Daily Shift Log — PROD-001A.
 *
 * Route: /production/daily-log
 *
 * Supervisor flow:
 *   1. Pick date + shift + (optional) department
 *   2. Click "Generate Drafts" → backend idempotently creates a
 *      ProductionLog per machine with checkpoints already laid out
 *      (Carding = 2, Ring Frame = 6 for a 12h shift)
 *   3. Each machine card shows: count / SO / opening stock / planned
 *      target / current status / efficiency-so-far
 *   4. Click a card → drawer with checkpoint tabs. Each tab asks for the
 *      MINIMUM fields (actual output, waste, transferred). System
 *      computes closing, efficiency, waste %.
 *   5. Submit checkpoint → ERP rolls totals up to the parent log
 *   6. One-click "Submit Shift" finalises everything
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, Col, DatePicker, Divider, Drawer, Empty,
  Form, Grid, Input, InputNumber, Modal, Progress, Row, Select, Space, Spin,
  Statistic, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  FileDoneOutlined, PlayCircleOutlined, ReloadOutlined, ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import {
  CHECKPOINT_STATUSES, EXCEPTION_REASONS, REASON_LABEL, STATUS_COLOR,
  checkpointsApi, type CardingWasteCategory, type Checkpoint, type DraftRow, type ExceptionReason,
} from '@/features/production/checkpointsApi'
import { cardingApi } from '@/features/production/cardingApi'
import { usePlantId } from '@/shared/hooks/usePlantId'
import apiClient from '@/shared/api/client'
import { filterShopFloorDepts } from '@/shared/constants/departments'
import { filterActiveShifts } from '@/shared/constants/shifts'

const { Title, Text, Paragraph } = Typography

interface ShiftLite { id: string; shift_code: string; shift_name: string }
interface DeptLite { id: string; name: string; department_type: string }

export default function DailyShiftLogPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [logDate, setLogDate] = useState(dayjs())
  const [shiftId, setShiftId] = useState<string | undefined>()
  const [departmentId, setDepartmentId] = useState<string | undefined>()
  const [activeDraft, setActiveDraft] = useState<DraftRow | null>(null)
  // Responsive — phone, tablet portrait, tablet landscape, desktop
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md  // <768
  const isTablet = !!screens.md && !screens.lg  // 768-992

  const shiftsQ = useQuery({
    enabled: !!plantId,
    queryKey: ['shifts-for-log', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/shifts')
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return filterActiveShifts(raw)
        .map((s: any): ShiftLite => ({
          id: s.id, shift_code: s.shift_code, shift_name: s.shift_name,
        }))
    },
  })
  const deptsQ = useQuery({
    enabled: !!plantId,
    queryKey: ['depts-for-log', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/departments', {
        params: { plant_id: plantId, is_active: true, size: 200 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      // Shop-floor only — hides Board of Directors / HR / Sales /
      // Accounts. Shared helper does the filter + process-flow sort.
      return filterShopFloorDepts(raw).map((d: any): DeptLite => ({
        id: d.id,
        // Backend exposes `name` (computed from department_name) +
        // `department_type`. Fall back to common alt-fields for safety.
        name: d.name ?? d.department_name ?? d.department_code ?? '—',
        department_type: d.department_type ?? '',
      }))
    },
  })

  const draftsQ = useQuery({
    enabled: !!plantId && !!shiftId,
    queryKey: ['drafts', plantId, logDate.format('YYYY-MM-DD'), shiftId, departmentId],
    queryFn: () => checkpointsApi.listDrafts({
      plant_id: plantId!,
      log_date: logDate.format('YYYY-MM-DD'),
      shift_id: shiftId!,
      department_id: departmentId,
    }),
  })

  const generateMut = useMutation({
    mutationFn: () => checkpointsApi.generateDrafts({
      plant_id: plantId!,
      log_date: logDate.format('YYYY-MM-DD'),
      shift_id: shiftId!,
      department_id: departmentId,
    }),
    onSuccess: (r) => {
      message.success(`${r.generated} drafts ready`)
      qc.invalidateQueries({ queryKey: ['drafts'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Generate failed'),
  })

  const drafts = draftsQ.data ?? []

  // KPI strip
  const kpis = useMemo(() => {
    const out = { total: 0, draft: 0, submitted: 0, approved: 0, output: 0 }
    for (const d of drafts) {
      out.total += 1
      if (d.status === 'DRAFT' || d.status === 'IN_PROGRESS') out.draft += 1
      if (d.status === 'SUBMITTED' || d.status === 'SUPERVISOR_REVIEW') out.submitted += 1
      if (d.status === 'APPROVED' || d.status === 'LOCKED') out.approved += 1
      out.output += d.actual_output_kg ?? 0
    }
    return out
  }, [drafts])

  // Group by department for grid display, sort cards within each group
  // by machine_code so CD-01..CD-14 / RF-01..RF-12 line up naturally
  // instead of by random UUID order.
  //
  // Dept groups are ordered by the canonical mill production route —
  // Blow Room first, Packing last — instead of alphabetically (which
  // put AUTO_CONER at the top, confusing operators). Mirrors
  // production_route_service.ROUTE_COMBED, with non-route depts
  // (HUMIDIFICATION / WCS / YCP / GENERAL) appended alphabetically.
  const DEPT_ROUTE_ORDER: string[] = [
    'BLOW_ROOM', 'BLOWROOM',
    'CARDING',
    'DRAW_FRAME_BREAKER',
    'LAP_FORMER',
    'COMBER',
    'DRAW_FRAME_FINISHER',
    'SIMPLEX',
    'RING_FRAME',
    'AUTO_CONER',
    'PACKING',
  ]
  const deptRank = (code: string): number => {
    const i = DEPT_ROUTE_ORDER.indexOf(code.toUpperCase())
    return i === -1 ? 999 : i
  }

  const byDept = useMemo(() => {
    const m = new Map<string, DraftRow[]>()
    for (const d of drafts) {
      const key = d.department_code || 'GENERAL'
      if (!m.has(key)) m.set(key, [])
      m.get(key)!.push(d)
    }
    for (const list of m.values()) {
      list.sort((a, b) =>
        (a.machine_code ?? '').localeCompare(b.machine_code ?? '', undefined, { numeric: true }),
      )
    }
    return [...m.entries()].sort(([a], [b]) => {
      const ra = deptRank(a)
      const rb = deptRank(b)
      if (ra !== rb) return ra - rb
      // Same rank (both 999 — i.e. both off-route) → alphabetic fallback.
      return a.localeCompare(b)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts])

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Title level={isMobile ? 4 : 3} style={{ marginBottom: 4 }}>Daily Shift Log</Title>
      {!isMobile && (
        <Text type="secondary">
          Drafts auto-generate from plan + worker allocation + standards.
          Supervisor enters only checkpoint actuals (output / waste / transfer);
          ERP calculates closing, efficiency, variance, and rolls totals up to
          the shift.
        </Text>
      )}

      {/* Filters + generate. On mobile each control is full-width and
          stacks vertically so blue-collar operators can tap with one
          thumb. Buttons are min 44px tall (AntD large = 40px, we pad). */}
      <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: isMobile ? 12 : 16 } }}>
        {isMobile ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <DatePicker
              value={logDate}
              onChange={(d) => d && setLogDate(d)}
              allowClear={false}
              size="large"
              style={{ width: '100%' }}
            />
            <Select
              size="large"
              style={{ width: '100%' }}
              placeholder="Pick shift"
              value={shiftId}
              onChange={setShiftId}
              options={(shiftsQ.data ?? []).map((s) => ({
                value: s.id, label: `${s.shift_code} — ${s.shift_name}`,
              }))}
              loading={shiftsQ.isLoading}
            />
            <Select
              size="large"
              allowClear
              showSearch
              style={{ width: '100%' }}
              placeholder="All departments"
              value={departmentId}
              onChange={setDepartmentId}
              options={(deptsQ.data ?? []).map((d) => ({
                value: d.id,
                label: d.department_type ? `${d.name} (${d.department_type})` : d.name,
              }))}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            <Space.Compact style={{ width: '100%' }}>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                disabled={!shiftId}
                loading={generateMut.isPending}
                onClick={() => generateMut.mutate()}
                style={{ flex: 1, height: 48 }}
              >
                Generate
              </Button>
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={() => draftsQ.refetch()}
                style={{ flex: 1, height: 48 }}
              >
                Refresh
              </Button>
            </Space.Compact>
          </Space>
        ) : (
          <Space wrap size={[12, 8]}>
            <Space size={6}>
              <Text strong>Date:</Text>
              <DatePicker value={logDate} onChange={(d) => d && setLogDate(d)} allowClear={false} />
            </Space>
            <Space size={6}>
              <Text strong>Shift:</Text>
              <Select
                style={{ width: 220 }}
                placeholder="Pick shift"
                value={shiftId}
                onChange={setShiftId}
                options={(shiftsQ.data ?? []).map((s) => ({
                  value: s.id, label: `${s.shift_code} — ${s.shift_name}`,
                }))}
                loading={shiftsQ.isLoading}
              />
            </Space>
            <Space size={6}>
              <Text>Department:</Text>
              <Select
                allowClear
                showSearch
                style={{ width: isTablet ? 220 : 280 }}
                placeholder="All departments"
                value={departmentId}
                onChange={setDepartmentId}
                options={(deptsQ.data ?? []).map((d) => ({
                  value: d.id,
                  label: d.department_type ? `${d.name} (${d.department_type})` : d.name,
                }))}
                filterOption={(input, option) =>
                  (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Space>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              disabled={!shiftId}
              loading={generateMut.isPending}
              onClick={() => generateMut.mutate()}
            >
              Generate Drafts
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => draftsQ.refetch()}>
              Refresh
            </Button>
          </Space>
        )}
      </Card>

      {/* KPI strip — compact on mobile (2 rows of 3), inline on desktop */}
      <Row gutter={[8, 8]} style={{ marginTop: 12, marginBottom: 12 }}>
        <Col xs={8} sm={8} md={5} lg={5} xl={4}>
          <KpiTile label="Drafts" value={kpis.total} color="#000" compact={isMobile} />
        </Col>
        <Col xs={8} sm={8} md={5} lg={5} xl={4}>
          <KpiTile label="Pending" value={kpis.draft} color={kpis.draft > 0 ? '#fa8c16' : '#000'} compact={isMobile} />
        </Col>
        <Col xs={8} sm={8} md={5} lg={5} xl={4}>
          <KpiTile label="Submitted" value={kpis.submitted} color="#1677ff" compact={isMobile} />
        </Col>
        <Col xs={12} sm={8} md={4} lg={4} xl={4}>
          <KpiTile label="Approved" value={kpis.approved} color="#52c41a" compact={isMobile} />
        </Col>
        <Col xs={12} sm={16} md={5} lg={5} xl={4}>
          <KpiTile
            label="Output (kg)"
            value={kpis.output.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            color="#1677ff"
            compact={isMobile}
          />
        </Col>
      </Row>

      {!shiftId && (
        <Alert
          type="info"
          showIcon
          message="Pick a shift to see today's drafts."
          style={{ marginBottom: 16 }}
        />
      )}

      {shiftId && draftsQ.isLoading && <Spin />}

      {shiftId && !draftsQ.isLoading && drafts.length === 0 && (
        <Empty
          description="No drafts yet for this date + shift"
          style={{ padding: 60 }}
        >
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => generateMut.mutate()}
            loading={generateMut.isPending}
          >
            Generate Drafts Now
          </Button>
        </Empty>
      )}

      {byDept.map(([dept, rows]) => (
        <div key={dept} style={{ marginBottom: 24 }}>
          <Divider orientation="left" style={{ marginTop: 0 }}>
            <Space>
              <Text strong>{dept}</Text>
              <Badge count={rows.length} style={{ backgroundColor: '#1677ff' }} />
            </Space>
          </Divider>
          {/* Sprint 0140 — Carding section closure banner.
              Above the 14 Carding cards: shows predicted waste
              (Σ per-card output × QC %) vs supervisor's combined
              actual entry. Saves once at shift end; high variance
              auto-notifies GM. Only renders for CARDING. */}
          {dept.toUpperCase() === 'CARDING' && plantId && shiftId && (
            <CardingSectionWasteBanner
              plantId={plantId}
              logDate={logDate.format('YYYY-MM-DD')}
              shiftId={shiftId}
            />
          )}
          {/* Sprint 0143 — Can summary. Auto-derived from cans table
              + production logs. Per-card breakdown injected as machineCanMap
              into each MachineCard for the secondary hero line. */}
          {dept.toUpperCase() === 'CARDING' && plantId && shiftId && (
            <CardingCanSummaryBanner
              plantId={plantId}
              logDate={logDate.format('YYYY-MM-DD')}
              shiftId={shiftId}
            />
          )}
          <Row gutter={[10, 10]}>
            {rows.map((r) => (
              // Phone: 1 card full-width — readable big numbers
              // Tablet portrait (sm/md): 2 cards
              // Tablet landscape (lg): 3 cards
              // Desktop (xl): 4 cards
              <Col xs={24} sm={12} md={12} lg={8} xl={6} key={r.id}>
                <MachineCard draft={r} onOpen={() => setActiveDraft(r)} />
              </Col>
            ))}
          </Row>
        </div>
      ))}

      <CheckpointDrawer
        draft={activeDraft}
        onClose={() => setActiveDraft(null)}
      />
    </div>
  )
}

// ─── KPI tile — tighter than Statistic, better on phones ─────────────

function KpiTile({
  label, value, color, compact,
}: { label: string; value: number | string; color: string; compact: boolean }) {
  return (
    <Card
      size="small"
      styles={{ body: { padding: compact ? 8 : 12 } }}
      style={{ borderRadius: 8 }}
    >
      <div style={{ fontSize: compact ? 10 : 12, color: '#8c8c8c', lineHeight: 1.2 }}>
        {label}
      </div>
      <div style={{
        fontSize: compact ? 18 : 22,
        fontWeight: 600,
        color,
        lineHeight: 1.2,
        marginTop: 2,
      }}>
        {value}
      </div>
    </Card>
  )
}

// ─── Metric cell — small label + bold value, for the 2x2 grid ────────

function MetricCell({
  label, value, valueColor, icon,
}: {
  label: string
  value: string
  valueColor?: string
  icon?: ReactNode
}) {
  return (
    <div style={{
      padding: '6px 8px',
      background: '#fafafa',
      borderRadius: 6,
      lineHeight: 1.2,
    }}>
      <div style={{
        fontSize: 10,
        color: '#8c8c8c',
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}>{label}</div>
      <div style={{
        fontSize: 14,
        fontWeight: 600,
        color: valueColor ?? '#262626',
        marginTop: 1,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {icon && <span style={{ marginRight: 4 }}>{icon}</span>}
        {value}
      </div>
    </div>
  )
}

// ─── Machine card ───────────────────────────────────────────────────

function MachineCard({ draft, onOpen }: { draft: DraftRow; onOpen: () => void }) {
  const screens = Grid.useBreakpoint()
  const isPhone = !screens.md  // <768
  const progress = draft.actual_output_kg ?? 0
  const planned = draft.expected_output_kg ?? draft.planned_output_kg ?? 0
  const pct = planned > 0 ? Math.round((progress / planned) * 100) : null
  const hasFlags = (draft.review_flags?.length ?? 0) > 0

  // Blue-collar: the operator's primary mental key is the machine
  // number (CD-01, RF-12). log_no (PRD-2026-0036) is a system
  // identifier and goes into the subtitle.
  const machineLabel = draft.machine_code || draft.machine_name || '(no machine)'

  // Status → colour + label. Blue-collar shorthand — operators say
  // "open / sent / done" not "DRAFT / SUBMITTED / APPROVED".
  const statusStyle: Record<string, { color: string; label: string }> = {
    DRAFT: { color: '#8c8c8c', label: 'Open' },
    IN_PROGRESS: { color: '#fa8c16', label: 'Running' },
    SUBMITTED: { color: '#1677ff', label: 'Sent' },
    SUPERVISOR_REVIEW: { color: '#1677ff', label: 'Review' },
    APPROVED: { color: '#52c41a', label: 'Done' },
    LOCKED: { color: '#722ed1', label: 'Locked' },
  }
  const stat = statusStyle[draft.status] ?? { color: '#8c8c8c', label: draft.status }
  const progressColor =
    pct == null ? '#1677ff'
      : pct >= 95 ? '#52c41a'
      : pct >= 70 ? '#1677ff'
      : pct >= 40 ? '#fa8c16'
      : '#cf1322'

  // Sprint 0139 — Blow Room mass-balance derive preview
  const isBlowRoom = (draft.department_code || '').toUpperCase() === 'BLOW_ROOM'
    || (draft.department_code || '').toUpperCase() === 'BLOWROOM'
  const brDeriveQ = useQuery({
    enabled: isBlowRoom,
    queryKey: ['br-derive', draft.id],
    queryFn: () => checkpointsApi.brDerivePreview(draft.id),
    refetchInterval: isBlowRoom ? 30_000 : false,
  })

  // Sprint 0139 — Carding per-card BR input share + live realization%
  const isCarding = (draft.department_code || '').toUpperCase() === 'CARDING'
  const cardingShareQ = useQuery({
    enabled: isCarding,
    queryKey: ['carding-input-share', draft.id],
    queryFn: () => checkpointsApi.cardingInputShare(draft.id),
    refetchInterval: isCarding ? 30_000 : false,
  })

  // Sprint 0143 — Cans-produced display for can-based depts.
  // Carding + DRAW_FRAME_BREAKER + DRAW_FRAME_FINISHER + COMBER all
  // produce sliver in cans. LAP_FORMER produces laps (handled
  // separately). std_can_kg comes from the dept's standards table via
  // autofetch (display_data) — falls back to 25 kg default.
  const deptUp = (draft.department_code || '').toUpperCase()
  const isCanProducingDept = isCarding
    || deptUp === 'DRAW_FRAME_BREAKER'
    || deptUp === 'DRAW_FRAME_FINISHER'
    || deptUp === 'COMBER'
  const DEFAULT_CAN_KG = 25
  // autofetch puts std_can_kg under display_data; also tolerate top-level
  // standard_can_kg if backend exposes it directly.
  const stdCanKg = isCanProducingDept
    ? Number(
        (draft as any).display_data?.standard_can_kg
          ?? (draft as any).std_can_kg
          ?? (draft as any).standard_can_kg
          ?? DEFAULT_CAN_KG
      )
    : DEFAULT_CAN_KG
  const cansProduced = isCanProducingDept && progress > 0 && stdCanKg > 0
    ? Math.round(progress / stdCanKg)
    : null
  const cansTarget = isCanProducingDept && planned > 0 && stdCanKg > 0
    ? Math.round(planned / stdCanKg)
    : null

  return (
    <Card
      hoverable
      size="small"
      onClick={onOpen}
      styles={{
        body: { padding: isPhone ? 12 : 14 },
        // Make the entire card a comfortable tap target on touch screens
      }}
      style={{
        borderRadius: 10,
        borderColor: hasFlags ? '#faad14' : undefined,
        borderWidth: hasFlags ? 1.5 : 1,
        cursor: 'pointer',
        minHeight: isPhone ? 0 : 200,
      }}
    >
      <Space direction="vertical" size={isPhone ? 8 : 6} style={{ width: '100%' }}>
        {/* Header row — Machine code BIG (primary mental key) + status pill */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: isPhone ? 22 : 20,
              fontWeight: 700,
              lineHeight: 1.1,
              color: '#262626',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {machineLabel}
            </div>
            {draft.machine_name && draft.machine_name !== draft.machine_code && (
              <Text type="secondary" style={{ fontSize: 11 }}>{draft.machine_name}</Text>
            )}
          </div>
          <Tag
            color={stat.color === '#8c8c8c' ? 'default' : undefined}
            style={{
              margin: 0,
              padding: '2px 10px',
              fontSize: 12,
              fontWeight: 600,
              background: stat.color === '#8c8c8c' ? undefined : stat.color,
              color: stat.color === '#8c8c8c' ? undefined : '#fff',
              borderColor: stat.color === '#8c8c8c' ? undefined : stat.color,
            }}
          >
            {stat.label}
          </Tag>
        </div>

        {/* Tags row — production type + lot. Compact, only when present. */}
        {(draft.production_type || draft.lot_no) && (
          <Space size={4} wrap>
            {draft.production_type && (
              <Tag
                color={draft.production_type === 'MAKE_TO_ORDER' ? 'blue' : 'default'}
                style={{ margin: 0, fontSize: 11 }}
              >
                {draft.production_type === 'MAKE_TO_ORDER' ? 'TO ORDER' : 'TO STOCK'}
              </Tag>
            )}
            {draft.lot_no && (
              <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>
                Lot {draft.lot_no}
              </Tag>
            )}
          </Space>
        )}

        {/* Hero number — output so far. The number operators care about. */}
        <div style={{
          background: '#fafafa',
          borderRadius: 8,
          padding: isPhone ? 10 : 8,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Output so far
          </div>
          <div style={{
            fontSize: isPhone ? 28 : 24,
            fontWeight: 700,
            color: progressColor,
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
            marginTop: 2,
          }}>
            {progress > 0 ? `${progress.toFixed(0)}` : '—'}
            <span style={{ fontSize: 13, fontWeight: 500, color: '#8c8c8c', marginLeft: 4 }}>kg</span>
          </div>
          {/* Sprint 0143 — Auto-derived can count for can-producing depts.
              Carding + Drawframe Breaker/Finisher + Comber. No operator
              input — output kg ÷ std_can_kg from the dept standards
              (25 kg/can default fallback). LAP_FORMER produces laps,
              not cans — handled in the form helper section. */}
          {isCanProducingDept && cansProduced !== null && (
            <div style={{ fontSize: 12, color: '#1677ff', marginTop: 2, fontWeight: 500 }}>
              ≈ {cansProduced} cans
              {cansTarget !== null && (
                <span style={{ color: '#8c8c8c', fontWeight: 400 }}>
                  {' / '}target {cansTarget}
                </span>
              )}
            </div>
          )}
          {planned > 0 && (
            <Progress
              percent={Math.min(pct ?? 0, 100)}
              showInfo={false}
              size="small"
              strokeColor={progressColor}
              style={{ marginTop: 4, marginBottom: 0 }}
            />
          )}
          {pct !== null && (
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
              {pct}% of {planned.toFixed(0)} kg target
            </div>
          )}
        </div>

        {/* Compact 2×2 metric grid — opening / target / efficiency. Big
            and tap-readable on phones. */}
        <Row gutter={[8, 6]}>
          <Col span={12}>
            <MetricCell
              label="Opening"
              value={
                draft.opening_stock_kg != null
                  ? `${draft.opening_stock_kg.toFixed(0)} kg`
                  : '—'
              }
            />
          </Col>
          <Col span={12}>
            <MetricCell
              label="Target"
              value={planned > 0 ? `${planned.toFixed(0)} kg` : '—'}
            />
          </Col>
          {draft.efficiency_pct != null && (
            <Col span={hasFlags ? 12 : 24}>
              <MetricCell
                label="Efficiency"
                value={`${draft.efficiency_pct.toFixed(1)}%`}
                valueColor={
                  draft.efficiency_pct >= 85 ? '#52c41a'
                    : draft.efficiency_pct >= 70 ? '#faad14'
                    : '#cf1322'
                }
              />
            </Col>
          )}
          {hasFlags && (
            <Col span={draft.efficiency_pct != null ? 12 : 24}>
              <MetricCell
                label="Flags"
                value={`${draft.review_flags!.length}`}
                valueColor="#faad14"
                icon={<WarningOutlined />}
              />
            </Col>
          )}
        </Row>

        {/* Log number — system ID, smallest text, last */}
        <Text type="secondary" style={{ fontSize: 10, textAlign: 'right' }}>
          {draft.log_no}
        </Text>


        {/* Blow Room mass-balance panel — Sprint 0139 */}
        {isBlowRoom && brDeriveQ.data && (
          <Card
            size="small"
            style={{
              marginTop: 4,
              borderColor: brDeriveQ.data.balance_closed ? '#52c41a' : '#faad14',
              background: '#fafafa',
            }}
            styles={{ body: { padding: 8 } }}
          >
            <Space direction="vertical" size={1} style={{ width: '100%' }}>
              <Space size={4} style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Derived from <Text strong>{brDeriveQ.data.card_logs_found}</Text> Card log(s)
                  {brDeriveQ.data.card_logs_pending > 0 && (
                    <> · <Text type="warning">{brDeriveQ.data.card_logs_pending} pending</Text></>
                  )}
                </Text>
                <Tag color={
                  brDeriveQ.data.confidence === 'EXACT' ? 'green'
                    : brDeriveQ.data.confidence === 'PARTIAL' ? 'gold'
                    : 'default'
                } style={{ fontSize: 10, marginRight: 0 }}>
                  {brDeriveQ.data.confidence}
                </Tag>
              </Space>
              <Text style={{ fontSize: 11 }}>
                BR Output: <Text strong>{brDeriveQ.data.br_output_kg.toFixed(1)} kg</Text>
                <Text type="secondary"> ({brDeriveQ.data.card_output_kg.toFixed(0)} out + {brDeriveQ.data.card_waste_kg.toFixed(0)} waste)</Text>
              </Text>
              <Text style={{ fontSize: 11 }}>
                Mass balance:{' '}
                <Text style={{ color: brDeriveQ.data.balance_closed ? '#52c41a' : '#faad14' }}>
                  {brDeriveQ.data.balance_closed ? '✓' : '⚠'} closing {brDeriveQ.data.closing_kg_computed.toFixed(1)} kg
                </Text>
              </Text>
            </Space>
          </Card>
        )}

        {/* Carding per-card realization panel — Sprint 0139 */}
        {isCarding && cardingShareQ.data && cardingShareQ.data.confidence !== 'DATA_NOT_AVAILABLE' && (() => {
          const d = cardingShareQ.data
          const realization = d.realization_pct ?? 0
          const realColor = realization >= 95 ? '#52c41a' : realization >= 90 ? '#faad14' : '#cf1322'
          return (
            <Card
              size="small"
              style={{
                marginTop: 4,
                borderColor: realColor,
                background: '#fafafa',
              }}
              styles={{ body: { padding: 8 } }}
            >
              <Space direction="vertical" size={1} style={{ width: '100%' }}>
                <Space size={4} style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Share of BR Output (1 of <Text strong>{d.n_cards_on_line}</Text> cards)
                  </Text>
                  <Tag color={
                    d.confidence === 'EXACT' ? 'green'
                      : d.confidence === 'PARTIAL' ? 'gold'
                      : 'default'
                  } style={{ fontSize: 10, marginRight: 0 }}>
                    {d.confidence}
                  </Tag>
                </Space>
                <Text style={{ fontSize: 11 }}>
                  Input share: <Text strong>{(d.input_share_kg ?? 0).toFixed(1)} kg</Text>
                  <Text type="secondary"> (BR {(d.br_output_kg ?? 0).toFixed(0)} ÷ {d.n_cards_on_line})</Text>
                </Text>
                <Text style={{ fontSize: 11 }}>
                  Realization:{' '}
                  <Text strong style={{ color: realColor }}>
                    {realization.toFixed(1)}%
                  </Text>
                  {d.waste_pct_of_input != null && (
                    <Text type="secondary"> · waste {d.waste_pct_of_input.toFixed(1)}%</Text>
                  )}
                </Text>
                {d.unaccounted_kg != null && Math.abs(d.unaccounted_kg) > 5 && (
                  <Text style={{ fontSize: 10, color: '#faad14' }}>
                    ⚠ {d.unaccounted_kg.toFixed(1)} kg unaccounted (fly/dust/error)
                  </Text>
                )}
              </Space>
            </Card>
          )
        })()}
      </Space>
    </Card>
  )
}

// ─── Checkpoint drawer ──────────────────────────────────────────────

function CheckpointDrawer({ draft, onClose }: {
  draft: DraftRow | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const cpsQ = useQuery({
    enabled: !!draft,
    queryKey: ['checkpoints', draft?.id],
    queryFn: () => checkpointsApi.listCheckpoints(draft!.id),
  })

  const submitShiftMut = useMutation({
    mutationFn: () => checkpointsApi.submitShift(draft!.id),
    onSuccess: (r) => {
      if (r.skipped_missing_actuals.length > 0) {
        message.warning(
          `Checkpoint(s) ${r.skipped_missing_actuals.join(', ')} still need actuals`,
        )
      } else if (r.errors.length > 0) {
        message.error(`Shift submit had ${r.errors.length} error(s)`)
      } else {
        message.success('Shift submitted')
      }
      qc.invalidateQueries({ queryKey: ['checkpoints'] })
      qc.invalidateQueries({ queryKey: ['drafts'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Shift submit failed'),
  })

  return (
    <Drawer
      title={draft ? (
        <Space size={6}>
          <Text strong style={{ fontSize: 16 }}>
            {draft.machine_code || draft.machine_name || '(no machine)'}
          </Text>
          <Tag>{draft.department_code}</Tag>
          <Tag color={STATUS_COLOR[draft.status as keyof typeof STATUS_COLOR] || 'default'}>
            {draft.status}
          </Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>{draft.log_no}</Text>
        </Space>
      ) : 'Checkpoints'}
      open={!!draft}
      onClose={onClose}
      width={720}
      extra={draft && (
        <Button
          type="primary"
          icon={<FileDoneOutlined />}
          onClick={() => submitShiftMut.mutate()}
          loading={submitShiftMut.isPending}
        >
          Submit Shift
        </Button>
      )}
      destroyOnClose
    >
      {draft && (
        <>
          <Paragraph type="secondary">
            Enter only output / waste / transferred for each checkpoint.
            Closing stock, efficiency, and variance auto-calculate. Late
            submissions need an exception reason.
          </Paragraph>
          {cpsQ.isLoading && <Spin />}
          {cpsQ.data && (
            <Tabs
              items={cpsQ.data.map((cp) => ({
                key: String(cp.checkpoint_no),
                label: (
                  <Space size={4}>
                    <Tag color={STATUS_COLOR[cp.status] || 'default'} style={{ marginRight: 0 }}>
                      CP {cp.checkpoint_no}
                    </Tag>
                    <Text style={{ fontSize: 11 }} type="secondary">
                      {dayjs(cp.checkpoint_start_time).format('HH:mm')}
                    </Text>
                    {/* Sprint 0141 — Punch time at-a-glance. Server-captured
                        submitted_at on every successful Submit. submitted_late
                        is true when punched > window_end + grace (15 min). */}
                    {cp.submitted_at && (
                      <Text
                        style={{ fontSize: 11 }}
                        type={cp.submitted_late ? 'danger' : 'success'}
                      >
                        {cp.submitted_late ? '⚠ ' : '✓ '}
                        {dayjs(cp.submitted_at).format('HH:mm')}
                      </Text>
                    )}
                  </Space>
                ),
                children: <CheckpointForm
                  cp={cp}
                  draft={draft}
                  totalCheckpoints={cpsQ.data!.length}
                />,
              }))}
            />
          )}
        </>
      )}
    </Drawer>
  )
}

// ─── Checkpoint form ────────────────────────────────────────────────

function CheckpointForm({ cp, draft, totalCheckpoints }: {
  cp: Checkpoint
  draft: DraftRow
  totalCheckpoints: number
}) {
  const draftId = draft.id
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const editable = cp.status === 'DRAFT' || cp.status === 'IN_PROGRESS'
  const [showException, setShowException] = useState(!!cp.exception_reason)

  // ─── Sprint 0146 QE merge — Hank + Faults state ───────────────
  // Lives in component state (not Form) because faults are SHIFT-level
  // (attached to the parent ProductionLog, not the per-checkpoint row).
  // Rehydrated from /production-logs/{id}/faults so reopening a
  // partially-filled drawer shows what was previously saved.
  type FaultType = 'DOUBLE_LAP' | 'OTHER_FAULTS' | 'SLIVER_BREAK'
                 | 'CHUTE_FAULTS' | 'DOFFS'
  const [hankProduced, setHankProduced] = useState<number | null>(null)
  const [faultCounts, setFaultCounts] = useState<Record<FaultType, number>>({
    DOUBLE_LAP: 0, OTHER_FAULTS: 0, SLIVER_BREAK: 0, CHUTE_FAULTS: 0, DOFFS: 0,
  })
  const [faultMeta, setFaultMeta] = useState<Record<FaultType, {
    root_cause: string | null; corrective_action: string | null
  }>>({
    DOUBLE_LAP:    { root_cause: null, corrective_action: null },
    OTHER_FAULTS:  { root_cause: null, corrective_action: null },
    SLIVER_BREAK:  { root_cause: null, corrective_action: null },
    CHUTE_FAULTS:  { root_cause: null, corrective_action: null },
    DOFFS:         { root_cause: null, corrective_action: null },
  })
  const [whyOpen, setWhyOpen] = useState<FaultType | null>(null)
  useQuery({
    queryKey: ['log-faults', draftId],
    queryFn: () => checkpointsApi.listLogFaults(draftId),
    enabled: !!draftId,
    refetchOnMount: 'always',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    select: (rows: any[]) => {
      if (!rows || rows.length === 0) return rows
      const newCounts = { ...faultCounts }
      const newMeta = { ...faultMeta }
      for (const r of rows) {
        const ft = r.fault_type as FaultType
        newCounts[ft] = r.count
        newMeta[ft] = {
          root_cause: r.root_cause,
          corrective_action: r.corrective_action,
        }
      }
      setFaultCounts(newCounts)
      setFaultMeta(newMeta)
      return rows
    },
  })

  // ─── Cross-department auto-fill from mass balance ───────────────
  // For BLOW_ROOM: derive Actual Output + Transferred from Carding logs
  //   (sum of all card outputs + waste), divided by N checkpoints.
  // For CARDING: derive Input Received from the per-card BR input
  //   share, divided by N checkpoints.
  // Suggestions only pre-fill when the checkpoint field is still null
  // — never overwrite operator-entered values.
  const dept = (draft.department_code || '').toUpperCase()
  const isBR = dept === 'BLOW_ROOM' || dept === 'BLOWROOM'
  const isCarding = dept === 'CARDING'

  // Sprint 0155 — Blow Room feed-readiness banner. Advisory only: the
  // backend guard still enforces at Submit, and supervisor override
  // still exists. Shown before the operator fills the form so they
  // don't complete the whole checkpoint only to be rejected at Submit.
  const { plantId } = usePlantId()
  const navigate = useNavigate()
  const brReadinessQ = useQuery({
    enabled: isBR && !!plantId,
    queryKey: ['blow-room-readiness', plantId],
    queryFn: async () => (
      await apiClient.get<{
        ready: boolean
        reasons: string[]
        message: string
        laydown_id: string | null
        input_cotton_kg: number | null
      }>('/blow-room-readiness', { params: { plant_id: plantId } })
    ).data,
  })
  // Sprint 0143 — Preparatory dept flags
  const isBreakerDF  = dept === 'DRAW_FRAME_BREAKER'
  const isFinisherDF = dept === 'DRAW_FRAME_FINISHER'
  const isDrawframe  = isBreakerDF || isFinisherDF
  const isLapFormer  = dept === 'LAP_FORMER'
  const isComber     = dept === 'COMBER'
  const isPreparatory = isDrawframe || isLapFormer || isComber

  // Pull display_data populated by autofetch_service (standards values).
  // For preparatory depts, this contains delivery_speed, hank, eff%,
  // std_can_kg, plus dept-specific extras (noil%, lap_weight, etc.).
  const dd = (draft as any).display_data ?? {}
  const stdEfficiency = Number(dd.standard_efficiency_pct ?? 88)
  const stdWastePct   = Number(dd.standard_waste_pct ?? 1)
  const stdCanKgForm  = Number(dd.standard_can_kg ?? 25)
  // Comber extras
  const stdNoilPct    = Number(dd.std_noil_pct ?? 18)
  const stdReconTolPct = Number(dd.reconciliation_tolerance_pct ?? 2)
  // Lap Former extras
  const stdLapWeightKg = Number(dd.std_lap_weight_kg ?? 0)
  const brDeriveQ = useQuery({
    enabled: isBR,
    queryKey: ['br-derive', draftId],
    queryFn: () => checkpointsApi.brDerivePreview(draftId),
  })
  const cardShareQ = useQuery({
    enabled: isCarding,
    queryKey: ['carding-input-share', draftId],
    queryFn: () => checkpointsApi.cardingInputShare(draftId),
  })
  const N = Math.max(1, totalCheckpoints)
  const brSuggestedOutput = isBR && brDeriveQ.data && brDeriveQ.data.br_output_kg > 0
    ? Math.round((brDeriveQ.data.br_output_kg / N) * 10) / 10
    : null
  // Sprint 0140 — BR Input comes from the linked laydown_execution
  // when one exists (net cotton fed to the line). Otherwise the
  // backend returns input = output (mass-balance neutral).
  const brSuggestedInput = isBR && brDeriveQ.data && brDeriveQ.data.suggested_input_kg > 0
    ? Math.round((brDeriveQ.data.suggested_input_kg / N) * 10) / 10
    : null
  // BR Waste = laydown net − BR output (the BR cleaning loss: trash,
  // sand, leaves, short fibre). Zero when laydown isn't linked yet.
  const brSuggestedWaste = isBR && brDeriveQ.data && brDeriveQ.data.suggested_waste_kg > 0
    ? Math.round((brDeriveQ.data.suggested_waste_kg / N) * 10) / 10
    : null
  const brInputSource = brDeriveQ.data?.input_source
  const cardSuggestedInput = isCarding
      && cardShareQ.data
      && cardShareQ.data.input_share_kg
      && cardShareQ.data.input_share_kg > 0
    ? Math.round((cardShareQ.data.input_share_kg / N) * 10) / 10
    : null
  // Sprint 0140 — Carding waste suggestion from CardProductionStandard
  // (default 4%). BR mass balance requires Carding waste to be non-zero
  // or BR Output (= Card Output + Card Waste) understates and BR Waste
  // overstates. Suggested figure ÷ N checkpoints. Operator can override.
  const cardSuggestedWaste = isCarding
      && cardShareQ.data
      && cardShareQ.data.suggested_waste_kg
      && cardShareQ.data.suggested_waste_kg > 0
    ? Math.round((cardShareQ.data.suggested_waste_kg / N) * 10) / 10
    : null
  const cardStandardWastePct = cardShareQ.data?.standard_waste_pct ?? null

  // Sprint 0141 — Reduce supervisor data entry to ~5 sec per card.
  // The checkpoint has expected_output_kg already computed from
  // CardProductionStandard × shift hours × efficiency. Use it to
  // pre-fill every field even when BR hasn't been submitted yet.
  // Supervisor glances at can count, hits Submit. Override only when
  // reality diverges from standard.
  //   Output      = expected_output_kg
  //   Waste       = output × standard_waste_pct/100
  //   Transferred = output  (sliver leaves the card to Drawing)
  //   Input       = output + waste  (chute received = output + waste)
  const cardWastePctForFill = cardStandardWastePct ?? 4
  const cardTargetOutput = isCarding && cp.expected_output_kg && cp.expected_output_kg > 0
    ? Math.round(cp.expected_output_kg * 10) / 10
    : null
  const cardTargetWaste = cardTargetOutput != null
    ? Math.round(cardTargetOutput * cardWastePctForFill / 100 * 10) / 10
    : null
  // Final per-field suggestions (BR-derived takes precedence when present,
  // standard-based fallback otherwise so the form is never empty).
  const cardFinalOutput = cardTargetOutput
  const cardFinalWaste = cardSuggestedWaste ?? cardTargetWaste
  const cardFinalInput = cardSuggestedInput ?? (
    cardTargetOutput != null && cardTargetWaste != null
      ? Math.round((cardTargetOutput + cardTargetWaste) * 10) / 10
      : null
  )
  const cardFinalTransferred = cardTargetOutput

  // Sprint 0146 QE merge — block submit when a fault count > 0 lacks
  // a root cause. Same enforcement as the standalone Quick Entry pages.
  const missingWhy: FaultType[] = useMemo(() => {
    const out: FaultType[] = []
    const NON_DOFFS: FaultType[] = [
      'DOUBLE_LAP', 'OTHER_FAULTS', 'SLIVER_BREAK', 'CHUTE_FAULTS',
    ]
    for (const ft of NON_DOFFS) {
      if (faultCounts[ft] > 0 && !faultMeta[ft].root_cause?.trim()) {
        out.push(ft)
      }
    }
    return out
  }, [faultCounts, faultMeta])

  const submitMut = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (v: any) => {
      // 1. Submit the checkpoint as before (existing behaviour preserved).
      const cpResult = await checkpointsApi.submitCheckpoint(cp.id, {
        actual_output_kg: v.actual_output_kg,
        input_received_kg: v.input_received_kg,
        transferred_qty_kg: v.transferred_qty_kg,
        waste_kg: v.waste_kg,
        rejection_kg: v.rejection_kg,
        adjustment_kg: v.adjustment_kg,
        physical_closing_stock_kg: v.physical_closing_stock_kg,
        downtime_minutes: v.downtime_minutes,
        idle_minutes: v.idle_minutes,
        power_fail_minutes: v.power_fail_minutes,
        operator_count: v.operator_count,
        exception_reason: v.exception_reason,
        exception_notes: v.exception_notes,
      })
      // 2. Sprint 0146 QE merge — also push faults to the parent log.
      //    Upsert semantics on the backend means it's safe to repeat;
      //    every checkpoint submit overwrites with the current state.
      //    Wrapped so a fault-write failure doesn't lose the checkpoint.
      try {
        const faults = (
          ['DOUBLE_LAP', 'OTHER_FAULTS', 'SLIVER_BREAK', 'CHUTE_FAULTS', 'DOFFS'] as FaultType[]
        )
          .filter((ft) => faultCounts[ft] > 0)
          .map((ft) => ({
            fault_type: ft,
            count: faultCounts[ft],
            root_cause: faultMeta[ft].root_cause,
            corrective_action: faultMeta[ft].corrective_action,
          }))
        await checkpointsApi.upsertLogFaults(draftId, faults)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        message.warning(
          'Checkpoint saved, but fault details did not — try again from the same drawer'
        )
      }
      return cpResult
    },
    onSuccess: () => {
      message.success(`Checkpoint ${cp.checkpoint_no} submitted`)
      qc.invalidateQueries({ queryKey: ['checkpoints', draftId] })
      qc.invalidateQueries({ queryKey: ['drafts'] })
      qc.invalidateQueries({ queryKey: ['log-faults', draftId] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Submit failed'),
  })

  const approveMut = useMutation({
    mutationFn: () => checkpointsApi.approveCheckpoint(cp.id),
    onSuccess: () => {
      message.success(`Checkpoint ${cp.checkpoint_no} approved`)
      qc.invalidateQueries({ queryKey: ['checkpoints', draftId] })
      qc.invalidateQueries({ queryKey: ['drafts'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Approve failed'),
  })

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={!editable}
      initialValues={{
        // Sprint 0141 — Auto-fill ALL 4 fields so supervisor only
        // confirms or overrides. ~5 sec per card instead of typing
        // 4 numbers. BR pulls from Carding mass-balance + laydown.
        // Carding falls back to expected_output_kg + standard waste %
        // when BR hasn't been submitted yet (the common case at
        // shift start) so form is never empty.
        actual_output_kg: cp.actual_output_kg
          ?? (isBR ? brSuggestedOutput ?? undefined
              : isCarding ? cardFinalOutput ?? undefined : undefined),
        // BR: every kg leaving the line is transferred (chute feed).
        // Carding: sliver leaves the card to Drawing — same as output.
        transferred_qty_kg: cp.transferred_qty_kg
          ?? (isBR ? brSuggestedOutput ?? undefined
              : isCarding ? cardFinalTransferred ?? undefined : undefined),
        // Input Received:
        //   BR      = laydown_execution.net_weight_kg ÷ N (operator never types)
        //   Carding = BR-derived share when BR submitted, else output+waste
        //             (chute receives output + waste back at this card).
        input_received_kg: cp.input_received_kg
          ?? (isBR ? brSuggestedInput ?? undefined
              : isCarding ? cardFinalInput ?? undefined : undefined),
        // Waste:
        //   BR      = laydown net − BR output (cleaning loss: trash,
        //             sand, leaves, short fibre).
        //   Carding = output × standard_waste_pct (4% default — flats,
        //             licker-in droppings, sliver clearer).
        //   Override at shift end when actual is weighed.
        waste_kg: cp.waste_kg
          ?? (isBR ? brSuggestedWaste ?? undefined
              : isCarding ? cardFinalWaste ?? undefined : undefined),
        rejection_kg: cp.rejection_kg ?? undefined,
        adjustment_kg: cp.adjustment_kg ?? undefined,
        physical_closing_stock_kg: cp.physical_closing_stock_kg ?? undefined,
        downtime_minutes: cp.downtime_minutes ?? undefined,
        idle_minutes: cp.idle_minutes ?? undefined,
        power_fail_minutes: cp.power_fail_minutes ?? undefined,
        operator_count: cp.operator_count ?? undefined,
        exception_reason: cp.exception_reason ?? undefined,
        exception_notes: cp.exception_notes ?? '',
      }}
      onFinish={(v) => submitMut.mutate(v)}
    >
      {/* Blow Room feed-readiness banner — Sprint 0155.
          Advisory only, shown BEFORE the input fields so the operator
          knows up front whether cotton feed is linked, instead of
          filling the whole checkpoint only to be rejected at Submit.
          The backend guard still enforces at submit time. */}
      {isBR && (
        brReadinessQ.isLoading ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Checking feed readiness…"
          />
        ) : brReadinessQ.data ? (
          brReadinessQ.data.ready ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 12 }}
              message="Feed ready"
              description={
                <span>
                  Laydown active
                  {brReadinessQ.data.input_cotton_kg != null && (
                    <> · {brReadinessQ.data.input_cotton_kg.toFixed(0)} kg issued</>
                  )}
                </span>
              }
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Blow Room feed not ready"
              description={
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <span>{brReadinessQ.data.message}</span>
                  <Button
                    size="small"
                    onClick={() => navigate('/bale-mgmt/laydown-execution')}
                  >
                    Go to Laydown Execution
                  </Button>
                </Space>
              }
            />
          )
        ) : null
      )}

      <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
        <Row gutter={[12, 4]}>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>Window</Text>
            <div>{dayjs(cp.checkpoint_start_time).format('HH:mm')} – {dayjs(cp.checkpoint_end_time).format('HH:mm')}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>Opening kg</Text>
            <div>{cp.opening_stock_kg?.toFixed(2) ?? '—'}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>Target kg</Text>
            <div>{cp.expected_output_kg?.toFixed(2) ?? '—'}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>System closing</Text>
            <div>{cp.closing_stock_kg?.toFixed(2) ?? '—'}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>Efficiency %</Text>
            <div>{cp.efficiency_pct?.toFixed(1) ?? '—'}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 11 }}>Variance kg</Text>
            <div>{cp.variance_kg?.toFixed(2) ?? '—'}</div>
          </Col>
        </Row>
      </Card>

      {/* Punch time banner — Sprint 0141.
          Shows server-captured submitted_at + submitted_by so the
          supervisor sees exactly when this checkpoint was logged
          vs the scheduled window. Late punches are flagged red
          and require an exception_reason (enforced server-side).
          Audit-ready — no edits possible client-side. */}
      {cp.submitted_at && (
        <Alert
          type={cp.submitted_late ? 'error' : 'success'}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              <Text strong>
                {cp.submitted_late ? 'Punched LATE' : 'Punched on time'}
              </Text>
              {' at '}
              <Text strong>{dayjs(cp.submitted_at).format('HH:mm:ss')}</Text>
              {' '}({dayjs(cp.submitted_at).format('DD MMM YYYY')})
              {cp.submitted_late && (
                <>
                  {' — '}<Text type="danger">
                    {dayjs(cp.submitted_at).diff(dayjs(cp.checkpoint_end_time), 'minute')} min after window close
                  </Text>
                </>
              )}
              {cp.exception_reason && (
                <>{' · '}<Text type="secondary">{cp.exception_reason}</Text></>
              )}
            </span>
          }
        />
      )}

      {/* BR mass-balance auto-fill banner — Sprint 0140
          Full chain: Laydown Net (cotton from bales after tare) feeds
          the BR. BR cleans (waste). BR Output goes to Carding.
            Input = Laydown Net ÷ N checkpoints
            Output = Σ Carding(output+waste) ÷ N
            Waste = Laydown Net − BR Output ÷ N  (BR cleaning loss)
            Transferred = Output
          If no laydown attached yet, backend falls back to
          input=output, waste=0 (still mass-balance neutral). */}
      {isBR && brSuggestedOutput != null && !cp.actual_output_kg && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              <Text strong>Auto-filled — no operator entry needed:</Text>{' '}
              Input <Text strong>{brSuggestedInput?.toFixed(0) ?? brSuggestedOutput.toFixed(0)} kg</Text>,
              {' '}Output <Text strong>{brSuggestedOutput.toFixed(0)} kg</Text>,
              {' '}Transferred <Text strong>{brSuggestedOutput.toFixed(0)} kg</Text>
              {brSuggestedWaste != null && (
                <>, Waste <Text strong>{brSuggestedWaste.toFixed(0)} kg</Text></>
              )}
              {' '}({
                brInputSource === 'LAYDOWN_NET'
                  ? `Input from laydown ${brDeriveQ.data?.laydown_net_kg?.toFixed(0)} kg net, Output ${brDeriveQ.data?.br_output_kg?.toFixed(0)} kg from Carding, ÷ ${N} checkpoints`
                  : brInputSource === 'OPERATOR_ENTRY'
                    ? `Input from supervisor entry ${brDeriveQ.data?.input_kg?.toFixed(0)} kg, Output ${brDeriveQ.data?.br_output_kg?.toFixed(0)} kg from Carding`
                    : `No laydown attached — using output ${brDeriveQ.data?.br_output_kg?.toFixed(0)} kg as input. Link a laydown to capture BR cleaning waste.`
              }).
            </span>
          }
        />
      )}

      {/* Carding auto-fill banner — Sprint 0141
          Single banner that explains BR-derived (preferred, accurate)
          OR standard-based fallback (when BR not yet submitted).
          Goal: supervisor opens form, sees ALL 4 fields pre-filled,
          glances at can count, hits Submit. ~5 sec per card. */}
      {isCarding && cardFinalOutput != null && !cp.actual_output_kg && (
        <Alert
          type={cardSuggestedInput != null ? 'info' : 'warning'}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              <Text strong>
                {cardSuggestedInput != null
                  ? 'Auto-filled from BR mass balance:'
                  : 'Auto-filled from standard (BR not yet submitted):'}
              </Text>
              {' '}Output <Text strong>{cardFinalOutput.toFixed(0)} kg</Text>,
              {' '}Waste <Text strong>{cardFinalWaste?.toFixed(1) ?? '—'} kg</Text>
              {' '}({cardStandardWastePct?.toFixed(1) ?? '4.0'}% std),
              {' '}Input <Text strong>{cardFinalInput?.toFixed(0) ?? '—'} kg</Text>,
              {' '}Transferred <Text strong>{cardFinalTransferred?.toFixed(0) ?? '—'} kg</Text>.
              {' '}<Text type="secondary">Glance at can count — Submit if it matches, override only what's different.</Text>
            </span>
          }
        />
      )}

      {/* Sprint 0143 — Preparatory dept standards banner.
          Shows the standards the autofetch used to compute target +
          dept-specific extras (noil%, lap weight, recon tolerance). */}
      {isPreparatory && (dd.delivery_speed_mpm || dd.output_hank) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              <Text strong>
                {isBreakerDF && 'Breaker Drawframe standard:'}
                {isFinisherDF && 'Finisher Drawframe standard:'}
                {isLapFormer && 'Lap Former standard:'}
                {isComber && 'Comber standard:'}
              </Text>
              {dd.delivery_speed_mpm && <> · Speed <b>{dd.delivery_speed_mpm} m/min</b></>}
              {dd.num_deliveries && <> · Deliveries <b>{dd.num_deliveries}</b></>}
              {dd.output_hank && <> · Hank <b>{dd.output_hank}</b></>}
              {dd.standard_efficiency_pct && <> · Eff <b>{dd.standard_efficiency_pct}%</b></>}
              {isComber && dd.std_noil_pct && <> · Noil <b>{dd.std_noil_pct}%</b></>}
              {isComber && dd.reconciliation_tolerance_pct && <> · Recon tol <b>±{dd.reconciliation_tolerance_pct}%</b></>}
              {isLapFormer && dd.std_lap_weight_kg && <> · Lap weight <b>{dd.std_lap_weight_kg} kg</b></>}
            </span>
          }
        />
      )}

      {/* Sprint 0143 — LAP_FORMER helper: laps × avg_weight = total kg.
          Operator counts laps + weighs one. Form converts to total
          output_kg automatically (set into actual_output_kg field). */}
      {isLapFormer && editable && (
        <Card size="small" style={{ marginBottom: 12, background: '#fffbe6', borderColor: '#ffe58f' }}>
          <Text strong>Quick output entry — laps × avg weight</Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4, marginBottom: 8 }}>
            Easier than weighing the whole shift output. Count finished laps,
            weigh one as average. Fills Actual Output kg automatically.
          </Paragraph>
          <Row gutter={12} align="middle">
            <Col span={8}>
              <Form.Item name="_laps_produced" label="Laps produced" style={{ marginBottom: 0 }}>
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                  onChange={(laps) => {
                    const avg = form.getFieldValue('_avg_lap_weight_kg') ?? stdLapWeightKg
                    if (laps != null && avg > 0) {
                      form.setFieldsValue({ actual_output_kg: Math.round(Number(laps) * Number(avg) * 100) / 100 })
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="_avg_lap_weight_kg"
                label="Avg lap weight kg"
                tooltip={stdLapWeightKg > 0 ? `Standard: ${stdLapWeightKg} kg` : undefined}
                style={{ marginBottom: 0 }}
                initialValue={stdLapWeightKg > 0 ? stdLapWeightKg : undefined}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  style={{ width: '100%' }}
                  onChange={(avg) => {
                    const laps = form.getFieldValue('_laps_produced')
                    if (laps != null && avg != null && Number(avg) > 0) {
                      form.setFieldsValue({ actual_output_kg: Math.round(Number(laps) * Number(avg) * 100) / 100 })
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>= total kg below</Text>
            </Col>
          </Row>
        </Card>
      )}

      {/* Sprint 0146 QE-merge v2 — Carding uses the LMW machine display
          layout as the primary entry surface. The existing mass-balance
          fields (Output / Input / Transferred / Waste / Rejection /
          Physical Closing) still get submitted to the backend — they're
          auto-derived from the LMW Kgs field + standards. Shown in a
          collapsible "Mass balance" callout below so the supervisor can
          verify or override when needed. */}
      {isCarding && (
        <CardingLMWMirror
          form={form}
          stdWastePct={stdWastePct}
          stdSliverHank={cardShareQ.data?.sliver_hank_ne ?? null}
          editable={editable}
        />
      )}

      {/* Mass-balance fields — primary for non-Carding, hidden behind a
          toggle for Carding (still in the form, still submitted, just
          collapsed since LMW Kgs already drives them). */}
      {!isCarding && (
        <Row gutter={12}>
          <Col span={8}><Form.Item name="actual_output_kg" label="Actual Output kg" rules={editable ? [{ required: true, message: 'Required' }] : []}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} autoFocus />
          </Form.Item></Col>
          <Col span={8}><Form.Item name="input_received_kg" label="Input Received kg">
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item></Col>
          <Col span={8}><Form.Item name="transferred_qty_kg" label="Transferred kg">
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item></Col>
        </Row>
      )}
      {isCarding && (
        // Same fields, in a compact collapsible callout. Form names match
        // so the submit body stays identical.
        <CardingMassBalanceCallout
          isComber={isComber}
          editable={editable}
        />
      )}
      {!isCarding && (
      <Row gutter={12}>
        <Col span={8}><Form.Item name="waste_kg" label="Waste kg">
          <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
        </Form.Item></Col>
        {/* Sprint 0143 — COMBER noil_kg. Operator weighs noil collection;
            value reconciles with output + waste vs input. We borrow the
            rejection_kg slot for noil since combers don't reject. */}
        {isComber ? (
          <Col span={8}><Form.Item
            name="rejection_kg"
            label={<Tooltip title="Noil from comber heads — combed-out short fibre. Goes to noil scrap.">Noil kg</Tooltip>}
          >
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
          </Form.Item></Col>
        ) : (
          <Col span={8}><Form.Item name="rejection_kg" label="Rejection kg">
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
          </Form.Item></Col>
        )}
        <Col span={8}><Form.Item
          name="physical_closing_stock_kg"
          label={<Tooltip title="Only if you physically counted closing stock">Physical Closing kg</Tooltip>}
        >
          <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
        </Form.Item></Col>
      </Row>
      )}

      {/* Sprint 0143 — COMBER live reconciliation.
          Per addendum Section 4: input_kg ≈ output_kg + noil_kg + waste_kg.
          When |input − accounted| / input > tolerance%, raise alert.
          Operator sees this BEFORE submitting so they can fix data entry. */}
      {isComber && editable && (
        <ComberReconciliationAlert
          form={form}
          tolerancePct={stdReconTolPct}
          stdNoilPct={stdNoilPct}
        />
      )}

      {/* Hank cross-check is now baked INTO the CardingLMWMirror above
          for Carding. (Older inline block removed in v2.) */}
      {void hankProduced /* kept for state-setter linter */}

      {/* Sprint 0146 QE merge — Fault counters with Why? drawers.
          Shows for Carding + 4 prep stages. Each count > 0 (excluding
          DOFFS) needs a root cause before the form can submit — the
          same discipline we use on the standalone Quick Entry pages. */}
      {(isCarding || isPreparatory) && editable && (
        <>
          <Divider style={{ margin: '12px 0' }}>
            Fault counters (shift total)
          </Divider>
          <CheckpointFaultRows
            counts={faultCounts}
            setCounts={setFaultCounts}
            meta={faultMeta}
            openWhy={(ft: FaultType) => setWhyOpen(ft)}
          />
          {missingWhy.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12, fontSize: 12 }}
              message={
                `Add the "Why?" for ${missingWhy.length} fault${missingWhy.length === 1 ? '' : 's'} `
                + `before submitting: ${missingWhy.join(', ')}`
              }
            />
          )}
        </>
      )}

      {/* Downtime + manpower — primary for non-Carding stages. For
          Carding the Run/Idle/PowerFail values are already captured by
          the LMW mirror above; we still show Operator count via a
          compact one-field row. */}
      {!isCarding && (
        <>
          <Divider style={{ margin: '12px 0' }}>Downtime + manpower (optional)</Divider>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="downtime_minutes" label="Downtime min">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item></Col>
            <Col span={6}><Form.Item name="idle_minutes" label="Idle min">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item></Col>
            <Col span={6}><Form.Item name="power_fail_minutes" label="Power fail min">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item></Col>
            <Col span={6}><Form.Item name="operator_count" label="Operator count">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item></Col>
          </Row>
        </>
      )}
      {isCarding && (
        <Row gutter={12} style={{ marginBottom: 8 }}>
          <Col span={6}><Form.Item name="operator_count" label="Operator count" style={{ marginBottom: 0 }}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item></Col>
        </Row>
      )}

      <Space size={4} style={{ marginBottom: 12 }}>
        <Button
          type="link"
          size="small"
          onClick={() => setShowException(!showException)}
        >
          {showException ? 'Hide exception' : '+ Add exception reason'}
        </Button>
      </Space>

      {showException && (
        <>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="exception_reason" label="Exception reason">
              <Select
                allowClear
                options={EXCEPTION_REASONS.map((r) => ({ value: r, label: REASON_LABEL[r] }))}
              />
            </Form.Item></Col>
            <Col span={12}><Form.Item name="exception_notes" label="Notes">
              <Input placeholder="What happened?" />
            </Form.Item></Col>
          </Row>
        </>
      )}

      {cp.submitted_late && (
        <Alert
          showIcon
          type="warning"
          message="Late submission — exception reason recommended"
          style={{ marginBottom: 12 }}
        />
      )}

      <Space>
        {editable && (
          <Button
            type="primary"
            htmlType="submit"
            icon={<PlayCircleOutlined />}
            loading={submitMut.isPending}
            // Sprint 0146 QE merge — block save until each fault > 0
            // has a root cause (DOFFS exempt). Banner above tells the
            // supervisor which ones are missing.
            disabled={missingWhy.length > 0}
          >
            Submit Checkpoint
          </Button>
        )}
        {cp.status === 'SUBMITTED' && (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => approveMut.mutate()}
            loading={approveMut.isPending}
          >
            Approve
          </Button>
        )}
        {(cp.status === 'APPROVED' || cp.status === 'LOCKED') && (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Approved {cp.approved_at ? dayjs(cp.approved_at).fromNow() : ''}
          </Tag>
        )}
        {cp.status === 'MISSED' && (
          <Tag color="red" icon={<ExclamationCircleOutlined />}>MISSED</Tag>
        )}
      </Space>

      {/* Sprint 0146 QE merge — Why? drawer for the fault rows above. */}
      <Drawer
        title={
          <Space>
            <WarningOutlined style={{ color: '#fa8c16' }} />
            <span>Why? — {whyOpen?.replace('_', ' ')}</span>
          </Space>
        }
        open={!!whyOpen}
        onClose={() => setWhyOpen(null)}
        width={420}
      >
        {whyOpen && (
          <CheckpointWhyForm
            faultType={whyOpen}
            dept={dept}
            initialCause={faultMeta[whyOpen].root_cause ?? ''}
            initialAction={faultMeta[whyOpen].corrective_action ?? ''}
            onSave={(m) => {
              setFaultMeta({ ...faultMeta, [whyOpen]: m })
              setWhyOpen(null)
            }}
          />
        )}
      </Drawer>
      {/* silence unused state setter when component unused on a stage */}
      {void hankProduced}
    </Form>
  )
}


// ─── Sprint 0146 QE merge sub-components ─────────────────────────────


/**
 * CardingLMWMirror — primary entry surface for Carding checkpoints.
 * Mirrors the LMW CARD LC 363 display 1:1 (Hank / Kgs / Run / Idle /
 * Power Fail / M/c Eff) so the supervisor types what they see, in the
 * order they see it. Writes form fields underneath:
 *   Kgs → actual_output_kg + transferred_qty_kg
 *   Run hh:mm → derived runtime_minutes (we don't have a form field;
 *               just keep it for display)
 *   Idle hh:mm → idle_minutes
 *   Power Fail hh:mm → power_fail_minutes
 *   M/c Eff % → goes into display_data on submit (server handles)
 * Plus a live hank↔kg consistency banner — catches sliver count drift.
 */
function CardingLMWMirror({
  form, stdWastePct, stdSliverHank, editable,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  stdWastePct: number
  /** Sliver hank Ne from CardProductionStandard — drives the Kgs ↔
   * Hank auto-calc. Null when no standard is set; both fields stay
   * manual and a hint asks the user to configure Carding Settings. */
  stdSliverHank: number | null
  editable: boolean
}) {
  // Local state for fields that DON'T have a backing form field yet
  // (Hank, M/c Eff%, the hh:mm helpers). For Kgs we DERIVE from
  // form.actual_output_kg via Form.useWatch so initialValues auto-fill
  // + Mass-balance overrides both flow through. Writing back goes
  // through form.setFieldsValue in writeForm() — never sets local kgs.
  const [hank, setHank] = useState<number | null>(null)
  const formOutput = Form.useWatch('actual_output_kg', form) as number | null | undefined
  const kgs: number | null = formOutput != null ? Number(formOutput) : null
  // Auto-fill bookkeeping. A field is "auto" until the user types into
  // it directly. While a field is auto, edits to the OTHER field
  // recompute it via the hanks formula. Once the user types into a
  // field, it's locked — the cross-check banner then surfaces any
  // drift between Kgs / Hank / std_sliver_hank.
  const [hankIsAuto, setHankIsAuto] = useState(true)
  const [kgsIsAuto, setKgsIsAuto] = useState(true)
  const [runH, setRunH] = useState<number | null>(null)
  const [runM, setRunM] = useState<number | null>(null)
  const [idleH, setIdleH] = useState<number | null>(() => {
    const v = form.getFieldValue('idle_minutes')
    return v ? Math.floor(v / 60) : null
  })
  const [idleM, setIdleM] = useState<number | null>(() => {
    const v = form.getFieldValue('idle_minutes')
    return v ? v % 60 : null
  })
  const [pfH, setPfH] = useState<number | null>(() => {
    const v = form.getFieldValue('power_fail_minutes')
    return v ? Math.floor(v / 60) : null
  })
  const [pfM, setPfM] = useState<number | null>(() => {
    const v = form.getFieldValue('power_fail_minutes')
    return v ? v % 60 : null
  })
  const [mcEff, setMcEff] = useState<number | null>(null)

  // ── Drive the underlying form fields whenever the LMW inputs change.
  // Output = Kgs, Transferred = Kgs (cards transfer everything out to
  // Drawing as sliver), Waste = Kgs × std_waste% (4% typical), Input is
  // left to autofetch / BR-derive.
  const writeForm = (next: { kgs?: number | null; idleMin?: number | null; pfMin?: number | null }) => {
    const patch: Record<string, unknown> = {}
    if (next.kgs !== undefined) {
      patch.actual_output_kg = next.kgs ?? undefined
      patch.transferred_qty_kg = next.kgs ?? undefined
      if (next.kgs != null) {
        patch.waste_kg = Math.round((next.kgs * stdWastePct / 100) * 10) / 10
      }
    }
    if (next.idleMin !== undefined) patch.idle_minutes = next.idleMin ?? undefined
    if (next.pfMin !== undefined) patch.power_fail_minutes = next.pfMin ?? undefined
    form.setFieldsValue(patch)
  }

  // Hanks formula (verified vs LMW display photos at 0.02%):
  //   hanks = kg × sliver_hank_Ne / 0.4535
  //   kg    = hanks × 0.4535 / sliver_hank_Ne
  const kgToHank = (k: number) => Math.round((k * (stdSliverHank ?? 0) / 0.4535) * 100) / 100
  const hankToKg = (h: number) => Math.round((h * 0.4535 / (stdSliverHank ?? 1)) * 10) / 10

  // Kgs ← user types. Always update form.actual_output_kg (canonical).
  // If Hank is still auto, recompute it from the new Kgs.
  const onKgsTyped = (v: number | null) => {
    setKgsIsAuto(false)
    writeForm({ kgs: v })
    if (hankIsAuto && stdSliverHank) {
      setHank(v != null ? kgToHank(v) : null)
    }
  }

  // Hank ← user types. Update local hank state. If Kgs is still auto,
  // recompute Kgs from the new Hank (goes through writeForm so the form
  // state, auto-derived waste, etc. all stay consistent).
  const onHankTyped = (v: number | null) => {
    setHankIsAuto(false)
    setHank(v)
    if (kgsIsAuto && stdSliverHank) {
      writeForm({ kgs: v != null ? hankToKg(v) : null })
    }
  }

  // Hank↔kg live cross-check. Only meaningful once at least ONE of the
  // two has been manually entered — until then both are either empty
  // or in lock-step from the formula and the banner would be noise.
  const check = useMemo(() => {
    if (!stdSliverHank || !kgs || !hank) return null
    if (hankIsAuto && kgsIsAuto) return null  // pure auto-derive, no drift possible
    const expected = (kgs * stdSliverHank) / 0.4535
    const dev = ((hank - expected) / expected) * 100
    const impliedHank = (hank * 0.4535) / kgs
    return {
      expected, dev, impliedHank,
      status: Math.abs(dev) <= 2 ? 'MATCH' : 'MISMATCH',
    }
  }, [kgs, hank, stdSliverHank, hankIsAuto, kgsIsAuto])

  const numField = (
    value: number | null, setter: (v: number | null) => void,
    placeholder = '', step = 1, onChange?: (v: number | null) => void,
  ) => (
    <InputNumber
      value={value} placeholder={placeholder} step={step}
      onChange={(v) => { setter(v as number | null); onChange?.(v as number | null) }}
      style={{ width: '100%', height: 40, fontSize: 15 }}
      disabled={!editable}
      inputMode="decimal"
    />
  )

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, background: '#f6fdff' }}
      title={
        <Space>
          <span>LMW display — type what you see on the machine</span>
          {stdSliverHank ? (
            <Tag>std sliver hank: {stdSliverHank} Ne</Tag>
          ) : (
            <Tag color="orange">no std — set Carding Setting first</Tag>
          )}
        </Space>
      }
    >
      <Row gutter={[12, 8]}>
        {/* Left column = production metrics on the LMW screen */}
        <Col xs={24} md={12}>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col span={10}>
              <Text>Hank</Text>
              {hankIsAuto && hank != null && (
                <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>auto</Tag>
              )}
            </Col>
            <Col span={14}>
              <InputNumber
                value={hank}
                placeholder="0050.31"
                step={0.01}
                onChange={(v) => onHankTyped(v as number | null)}
                style={{ width: '100%', height: 40, fontSize: 15 }}
                disabled={!editable}
                inputMode="decimal"
              />
            </Col>
          </Row>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col span={10}>
              <Text>Kgs</Text>
              {kgsIsAuto && kgs != null && (
                <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>auto</Tag>
              )}
            </Col>
            <Col span={14}>
              {/* Kgs is the canonical bridge — its writes go directly
                  to the form state via writeForm(). No local setter
                  needed because kgs is derived from Form.useWatch. */}
              <InputNumber
                value={kgs}
                placeholder="0207.44"
                step={0.1}
                onChange={(v) => onKgsTyped(v as number | null)}
                style={{ width: '100%', height: 40, fontSize: 15 }}
                disabled={!editable}
                inputMode="decimal"
              />
            </Col>
          </Row>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col span={10}><Text>Run Time</Text></Col>
            <Col span={14}>
              <Space.Compact style={{ width: '100%' }}>
                {numField(runH, setRunH, 'hh')}
                {numField(runM, setRunM, 'mm')}
              </Space.Compact>
            </Col>
          </Row>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col span={10}><Text>Idle Time</Text></Col>
            <Col span={14}>
              <Space.Compact style={{ width: '100%' }}>
                {numField(idleH, setIdleH, 'hh', 1, (h) => writeForm({
                  idleMin: ((h ?? 0) * 60) + (idleM ?? 0),
                }))}
                {numField(idleM, setIdleM, 'mm', 1, (m) => writeForm({
                  idleMin: ((idleH ?? 0) * 60) + (m ?? 0),
                }))}
              </Space.Compact>
            </Col>
          </Row>
          <Row gutter={8} style={{ marginBottom: 6 }}>
            <Col span={10}><Text>Power Fail</Text></Col>
            <Col span={14}>
              <Space.Compact style={{ width: '100%' }}>
                {numField(pfH, setPfH, 'hh', 1, (h) => writeForm({
                  pfMin: ((h ?? 0) * 60) + (pfM ?? 0),
                }))}
                {numField(pfM, setPfM, 'mm', 1, (m) => writeForm({
                  pfMin: ((pfH ?? 0) * 60) + (m ?? 0),
                }))}
              </Space.Compact>
            </Col>
          </Row>
          <Row gutter={8} style={{ marginBottom: 0 }}>
            <Col span={10}><Text>M/c Eff (%)</Text></Col>
            <Col span={14}>{numField(mcEff, setMcEff, '58.49', 0.01)}</Col>
          </Row>
        </Col>

        {/* Right column = live cross-check + helpful notes */}
        <Col xs={24} md={12}>
          {check ? (
            check.status === 'MATCH' ? (
              <Alert
                type="success" showIcon
                message={`✓ Hank ↔ Kg match`}
                description={
                  <Text style={{ fontSize: 12 }}>
                    Expected {check.expected.toFixed(2)} hanks at {kgs} kg
                    {' — '}{check.dev > 0 ? '+' : ''}{check.dev.toFixed(2)}% deviation.
                  </Text>
                }
              />
            ) : (
              <Alert
                type="warning" showIcon
                message={`⚠ Hank ↔ Kg mismatch`}
                description={
                  <Text style={{ fontSize: 12 }}>
                    Expected {check.expected.toFixed(2)} hanks, got {hank}
                    {' '}({check.dev > 0 ? '+' : ''}{check.dev.toFixed(2)}%).
                    Implied sliver hank: {check.impliedHank.toFixed(4)} Ne.
                    Schedule a QC sample.
                  </Text>
                }
              />
            )
          ) : stdSliverHank ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Type either Kgs or Hank — the other auto-fills at sliver
              {' '}hank <b>{stdSliverHank} Ne</b>. Override either to
              {' '}cross-check against the standard.
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Set the Carding sliver hank Ne in Settings to enable
              {' '}Kgs ↔ Hank auto-fill.
            </Text>
          )}
        </Col>
      </Row>
    </Card>
  )
}


/**
 * Collapsible callout for the 6 mass-balance fields when isCarding.
 *
 * IMPORTANT — fields are ALWAYS mounted (just visually hidden when
 * collapsed). AntD doesn't reliably preserve values for Form.Items
 * that have never been mounted, so we keep them in the tree and just
 * toggle the wrapper's display. This way the LMW Kgs auto-write into
 * actual_output_kg actually reaches the submit payload.
 */
function CardingMassBalanceCallout({
  isComber, editable,
}: { isComber: boolean; editable: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <Button
            type="link" size="small"
            onClick={() => setOpen(!open)}
            style={{ padding: 0 }}
          >
            {open ? '▼' : '▶'} Mass balance (auto-derived from Kgs)
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Override only what the LMW screen doesn&apos;t cover
          </Text>
        </Space>
      }
    >
      <div style={{ display: open ? 'block' : 'none' }}>
        <Row gutter={12}>
          <Col span={8}><Form.Item name="actual_output_kg" label="Actual Output kg" rules={editable ? [{ required: true, message: 'Required' }] : []}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item></Col>
          <Col span={8}><Form.Item name="input_received_kg" label="Input Received kg">
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item></Col>
          <Col span={8}><Form.Item name="transferred_qty_kg" label="Transferred kg">
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item></Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}><Form.Item name="waste_kg" label="Waste kg">
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
          </Form.Item></Col>
          <Col span={8}><Form.Item
            name="rejection_kg"
            label={isComber ? <Tooltip title="Noil from comber heads">Noil kg</Tooltip> : 'Rejection kg'}
          >
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
          </Form.Item></Col>
          <Col span={8}><Form.Item
            name="physical_closing_stock_kg"
            label={<Tooltip title="Only if you physically counted closing stock">Physical Closing kg</Tooltip>}
          >
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item></Col>
        </Row>
      </div>
    </Card>
  )
}


/**
 * Carding-only live hank↔kg cross-check. The LMW "Hank" field on the
 * machine display is PRODUCTION VOLUME in hank units (not the sliver
 * count). We let the supervisor type it and compare against what the
 * sliver hank from the standards master says it should be at the
 * current Output kg. Catches sliver count drift at entry.
 */
function CardingHankCrossCheck({
  form, hank, onHankChange, stdSliverHank,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  hank: number | null
  onHankChange: (v: number | null) => void
  stdSliverHank: number | null
}) {
  const outputKg = Number(Form.useWatch('actual_output_kg', form) ?? 0)
  const check = useMemo(() => {
    if (!stdSliverHank || !outputKg || !hank) return null
    const expected = (outputKg * stdSliverHank) / 0.4535
    const dev = ((hank - expected) / expected) * 100
    const impliedHank = (hank * 0.4535) / outputKg
    return {
      expected, dev, impliedHank,
      status: Math.abs(dev) <= 2 ? 'MATCH' : 'MISMATCH',
    }
  }, [outputKg, hank, stdSliverHank])
  return (
    <Card
      size="small"
      style={{ marginBottom: 12, background: '#f6fdff' }}
      title={
        <Space>
          <span>LMW &quot;Hank&quot; field (optional)</span>
          {stdSliverHank ? (
            <Tag>std sliver hank: {stdSliverHank} Ne</Tag>
          ) : (
            <Tag color="orange">no std — set Carding Setting first</Tag>
          )}
        </Space>
      }
    >
      <Row gutter={12} align="middle">
        <Col span={8}>
          <InputNumber
            min={0} step={0.01}
            value={hank}
            onChange={(v) => onHankChange(v as number | null)}
            placeholder="0050.31"
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={16}>
          {check ? (
            check.status === 'MATCH' ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                ✓ MATCH — expected {check.expected.toFixed(2)} hanks
                ({check.dev.toFixed(2)}% deviation)
              </Tag>
            ) : (
              <Tooltip title={`Implied sliver hank from your inputs: ${check.impliedHank.toFixed(4)} Ne`}>
                <Tag color="orange" icon={<WarningOutlined />}>
                  ⚠ MISMATCH — expected {check.expected.toFixed(2)} hanks
                  ({check.dev > 0 ? '+' : ''}{check.dev.toFixed(2)}%) ·
                  schedule QC
                </Tag>
              </Tooltip>
            )
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Enter Output kg + Hank to see the cross-check
            </Text>
          )}
        </Col>
      </Row>
    </Card>
  )
}


type _FT = 'DOUBLE_LAP' | 'OTHER_FAULTS' | 'SLIVER_BREAK' | 'CHUTE_FAULTS' | 'DOFFS'


/** Five rows of fault counters — same structure as the standalone Quick
 *  Entry pages. Each row gets a Why? button when count > 0. */
function CheckpointFaultRows({
  counts, setCounts, meta, openWhy,
}: {
  counts: Record<_FT, number>
  setCounts: (c: Record<_FT, number>) => void
  meta: Record<_FT, { root_cause: string | null; corrective_action: string | null }>
  openWhy: (ft: _FT) => void
}) {
  const rows: Array<[_FT, string, boolean]> = [
    ['DOUBLE_LAP',   'Double Lap',    false],
    ['OTHER_FAULTS', 'Other Faults',  false],
    ['SLIVER_BREAK', 'Sliver Break',  false],
    ['CHUTE_FAULTS', 'Chute Faults',  false],
    ['DOFFS',        'No of Doffs',   true ],  // DOFFS — no Why? needed
  ]
  return (
    <div style={{ marginBottom: 8 }}>
      {rows.map(([ft, label, noWhy]) => {
        const c = counts[ft]
        const hasWhy = !!meta[ft].root_cause
        const needsWhy = c > 0 && !hasWhy && !noWhy
        return (
          <Row key={ft} gutter={8} align="middle" style={{ marginBottom: 6 }}>
            <Col span={10}><Text>{label}</Text></Col>
            <Col span={8}>
              <InputNumber
                min={0} value={c}
                onChange={(v) => setCounts({ ...counts, [ft]: Number(v ?? 0) })}
                style={{ width: '100%' }} inputMode="numeric"
              />
            </Col>
            <Col span={6}>
              {!noWhy && c > 0 && (
                <Button
                  danger={needsWhy}
                  size="small"
                  type={needsWhy ? 'primary' : 'default'}
                  onClick={() => openWhy(ft)}
                  block
                >
                  {hasWhy ? '✓ Why?' : 'Why?'}
                </Button>
              )}
            </Col>
          </Row>
        )
      })}
    </div>
  )
}


// Per-stage root-cause chips. Carding has its own set; the 4 prep depts
// share a pool because the failure modes overlap (Drawing / Combing /
// Lap Former are all sliver-shaping operations).
const _ROOT_CHIPS: Record<string, Record<_FT, string[]>> = {
  CARDING: {
    DOUBLE_LAP:   ['Cotton sticky', 'Chute pressure low', 'Feed roller jam', 'Photocell dirty'],
    SLIVER_BREAK: ['Trumpet blocked', 'Draft too high', 'Sliver too thin', 'Calender slip'],
    CHUTE_FAULTS: ['Chute pressure', 'Photocell dirty', 'Flap stuck', 'Bridging'],
    OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical', 'Operator'],
    DOFFS:        [],
  },
  PREP: {
    DOUBLE_LAP:   ['Creel slip', 'Sliver too thin', 'Lap wrap break'],
    SLIVER_BREAK: ['Trumpet blocked', 'Draft too high', 'Tension low'],
    CHUTE_FAULTS: ['n/a'],
    OTHER_FAULTS: ['Power dip', 'Mechanical', 'Electrical'],
    DOFFS:        [],
  },
}

const _ACTION_CHIPS: Record<_FT, string[]> = {
  DOUBLE_LAP:   ['Cleared jam', 'Reset creel', 'Replaced sliver'],
  SLIVER_BREAK: ['Cleaned trumpet', 'Adjusted draft', 'Replaced guide'],
  CHUTE_FAULTS: ['Reset pressure', 'Cleaned sensor', 'Freed flap'],
  OTHER_FAULTS: ['Restarted', 'Called maintenance', 'Logged for next shift'],
  DOFFS:        [],
}


/** Compact Why? form — chips + free text for root cause + action. */
function CheckpointWhyForm({
  faultType, dept, initialCause, initialAction, onSave,
}: {
  faultType: _FT
  dept: string
  initialCause: string
  initialAction: string
  onSave: (m: { root_cause: string | null; corrective_action: string | null }) => void
}) {
  const [cause, setCause] = useState(initialCause)
  const [action, setAction] = useState(initialAction)
  const chipSet = dept === 'CARDING' ? _ROOT_CHIPS.CARDING : _ROOT_CHIPS.PREP
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div>
        <Text strong>Root cause</Text>
        <Space wrap size={[6, 6]} style={{ marginTop: 6, marginBottom: 6 }}>
          {chipSet[faultType].map((c) => (
            <Tag.CheckableTag
              key={c} checked={cause === c}
              onChange={() => setCause(c)}
            >
              {c}
            </Tag.CheckableTag>
          ))}
        </Space>
        <Input.TextArea
          rows={2} value={cause}
          onChange={(e) => setCause(e.target.value)}
          placeholder="Tap a chip or type your own"
        />
      </div>
      <div>
        <Text strong>Corrective action taken</Text>
        <Space wrap size={[6, 6]} style={{ marginTop: 6, marginBottom: 6 }}>
          {_ACTION_CHIPS[faultType].map((c) => (
            <Tag.CheckableTag
              key={c} checked={action === c}
              onChange={() => setAction(c)}
            >
              {c}
            </Tag.CheckableTag>
          ))}
        </Space>
        <Input.TextArea
          rows={2} value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="What did you do about it?"
        />
      </div>
      <Button
        type="primary" block disabled={!cause.trim()}
        onClick={() => onSave({
          root_cause: cause.trim() || null,
          corrective_action: action.trim() || null,
        })}
      >
        Save reason
      </Button>
    </Space>
  )
}

// dayjs relativeTime
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// silence unused imports
void Badge
void Modal
void Table
void CHECKPOINT_STATUSES

// ─── Sprint 0140 — Carding Section Waste banner ──────────────────────────
// End-of-shift closure: Σ predicted (per-card output × QC %) vs supervisor's
// combined actual entry. High variance auto-notifies GM. Reports use ACTUAL.
function CardingSectionWasteBanner({
  plantId, logDate, shiftId,
}: {
  plantId: string
  logDate: string
  shiftId: string
}) {
  const qc = useQueryClient()
  // Sprint carding-waste-categories — per-category kg map, keyed by category id
  const [perCat, setPerCat] = useState<Record<string, number | null>>({})
  const [notes, setNotes] = useState('')
  const [editing, setEditing] = useState(false)
  // Sprint 0198 — Blow Room + Carding section mass-balance reconciliation
  const [contamination, setContamination] = useState<number | null>(null)
  const [rejection, setRejection] = useState<number | null>(null)

  // Fix #4 — a half-filled editor must not survive a shift/date switch:
  // reset every editable field when the identity of "which shift" changes.
  useEffect(() => {
    setPerCat({})
    setNotes('')
    setContamination(null)
    setRejection(null)
    setEditing(false)
  }, [plantId, logDate, shiftId])

  const categoriesQ = useQuery({
    queryKey: ['carding-waste-categories'],
    queryFn: () => checkpointsApi.getCardingWasteCategories({ active_only: true }),
  })
  // Memoised so the `editorCategories` useMemo below gets a stable reference —
  // a bare `?? []` allocates a fresh array every render while the query loads.
  const categories = useMemo(() => categoriesQ.data ?? [], [categoriesQ.data])

  const summaryQ = useQuery({
    enabled: !!plantId && !!shiftId,
    queryKey: ['carding-section-waste', plantId, logDate, shiftId],
    queryFn: () => checkpointsApi.getCardingSectionWaste({
      plant_id: plantId, log_date: logDate, shift_id: shiftId,
    }),
    refetchInterval: 30_000,  // sync if another browser saved
  })
  const saved = summaryQ.data?.saved ?? null

  // Fix #2 — the editor's category list is the union of currently-active
  // categories AND any category referenced by the saved closure that has
  // since been deactivated. Without this, a stale saved line renders no
  // input (invisible), yet still inflated the old totalKg, and got
  // silently dropped from `lines` on save. totalKg and the save payload
  // both derive from this SAME list so the displayed total always equals
  // exactly what gets persisted.
  const editorCategories = useMemo<CardingWasteCategory[]>(() => {
    const activeIds = new Set(categories.map((c) => c.id))
    const inactiveFromSaved: CardingWasteCategory[] = (saved?.lines ?? [])
      .filter((l) => !activeIds.has(l.category_id))
      .map((l) => ({
        id: l.category_id, code: l.code, name: `${l.name} (inactive)`,
        sort_order: 9999, is_active: false,
      }))
    return [...categories, ...inactiveFromSaved]
  }, [categories, saved])

  const totalKg = editorCategories.reduce((s, c) => s + (perCat[c.id] ?? 0), 0)

  const saveMut = useMutation({
    mutationFn: () => checkpointsApi.saveCardingSectionWaste({
      plant_id: plantId, log_date: logDate, shift_id: shiftId,
      // Fix #B — correcting a legacy closure (no per-category lines yet)
      // without splitting it: omit `lines` entirely so the server takes
      // the verbatim legacy path (waste_lines=None) instead of deriving
      // a total of 0 from an empty array, which would wipe the figure.
      actual_waste_kg: totalKg === 0 && legacyTotal != null ? legacyTotal : totalKg,
      lines: totalKg === 0 && legacyTotal != null
        ? undefined
        : editorCategories
          .filter((c) => (perCat[c.id] ?? 0) > 0)
          .map((c) => ({ category_id: c.id, kg: perCat[c.id] as number })),
      contamination_kg: contamination ?? 0,
      rejection_kg: rejection ?? 0,
      notes: notes || undefined,
    }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['carding-section-waste'] })
      setEditing(false)
      setPerCat({})
      if (data.breached_threshold) {
        message.warning(
          `Variance ${data.variance_pct > 0 ? '+' : ''}${data.variance_pct.toFixed(1)}% exceeds threshold — escalated for cross-check.`,
        )
      } else {
        message.success(
          `Saved. Variance ${data.variance_pct > 0 ? '+' : ''}${data.variance_pct.toFixed(1)}% (within ${data.threshold_pct_at_save}% threshold).`,
        )
      }
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  if (!summaryQ.data) return null
  const { predicted_waste_kg, n_cards, threshold_pct } = summaryQ.data
  // Show banner once Carding cards have output (predicted > 0) OR already saved
  if (predicted_waste_kg <= 0 && !saved) return null

  const isSaved = saved !== null
  const breached = saved?.breached_threshold ?? false
  const variancePct = saved?.variance_pct ?? 0
  const varianceSign = variancePct > 0 ? '+' : ''

  const categoriesLoading = categoriesQ.isLoading
  const categoriesEmpty = !categoriesLoading && editorCategories.length === 0
  // Fix #3 — a closure saved before this feature has actual_waste_kg but
  // no per-category lines. Surface that old figure inside the editor so
  // correcting it isn't a blind guess.
  const legacyTotal = saved && (saved.lines?.length ?? 0) === 0 ? saved.actual_waste_kg : null

  // Consolidated editor — used both for the not-yet-saved entry flow and
  // for correcting an already-saved closure. One InputNumber per active
  // (+ stale-but-referenced) waste category + contamination/rejection/notes
  // + a live total. Fix #1 — every branch, including the loading/empty
  // dead ends, always offers a way back out of edit mode.
  function renderEditor() {
    const cancelBtn = (
      <Button size="small" onClick={() => { setEditing(false); setPerCat({}) }}>Cancel</Button>
    )
    if (categoriesLoading) {
      return (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>Loading waste categories…</Text>
          {cancelBtn}
        </>
      )
    }
    if (categoriesEmpty) {
      return (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            No waste categories configured — ask an admin to set them up on Carding Settings.
          </Text>
          {cancelBtn}
        </>
      )
    }
    return (
      <>
        {legacyTotal != null && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Previously recorded: <Text strong style={{ fontSize: 12 }}>{legacyTotal.toFixed(1)} kg</Text>
            {' '}— split it across the categories below.
          </Text>
        )}
        {editorCategories.map((c) => (
          <InputNumber
            key={c.id}
            min={0}
            step={0.5}
            addonBefore={c.name}
            placeholder="kg"
            value={perCat[c.id] ?? undefined}
            onChange={(v) => setPerCat((prev) => ({ ...prev, [c.id]: v ?? null }))}
            style={{ width: 130 }}
          />
        ))}
        <InputNumber
          min={0}
          step={0.5}
          placeholder="Contamination (kg)"
          value={contamination}
          onChange={(v) => setContamination(v ?? null)}
          style={{ width: 160 }}
        />
        <InputNumber
          min={0}
          step={0.5}
          placeholder="Rejection (kg)"
          value={rejection}
          onChange={(v) => setRejection(v ?? null)}
          style={{ width: 160 }}
        />
        <Input
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ width: 220 }}
        />
        <Text strong>Total swept: {totalKg.toFixed(1)} kg</Text>
        <Button
          type="primary" size="small"
          loading={saveMut.isPending}
          disabled={totalKg <= 0 && legacyTotal == null}
          onClick={() => saveMut.mutate()}
        >Save</Button>
        {cancelBtn}
      </>
    )
  }

  return (
    <Alert
      type={isSaved ? (breached ? 'error' : 'success') : 'warning'}
      showIcon
      style={{ marginBottom: 12 }}
      message={
        <Space wrap size="middle">
          <span>
            <Text strong>Carding section waste closure:</Text>{' '}
            Predicted{' '}
            <Text strong>{predicted_waste_kg.toFixed(1)} kg</Text>{' '}
            <Text type="secondary">
              (Σ {n_cards} cards × QC %)
            </Text>
          </span>
          {isSaved && !editing && (
            <>
              <span>
                Actual <Text strong>{saved!.actual_waste_kg.toFixed(1)} kg</Text>
              </span>
              {saved!.lines && saved!.lines.length > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {saved!.lines.map((l) => `${l.name} ${l.kg.toFixed(1)}`).join(' · ')}
                </Text>
              )}
              <Tag color={breached ? 'red' : 'green'}>
                Variance {varianceSign}{variancePct.toFixed(1)}% (threshold ±{saved!.threshold_pct_at_save}%)
              </Tag>
              {breached && saved!.gm_notification_id && (
                <Tag color="volcano">Escalated</Tag>
              )}
              {saved?.feed_kg != null && (
                <span>
                  Section balance: Feed <Text strong>{saved.feed_kg.toFixed(0)} kg</Text> −
                  (Output {saved.output_kg?.toFixed(0)} + losses) →{' '}
                  <Text strong type={saved.balance_breached ? 'danger' : 'success'}>
                    {saved.balance_variance_kg != null
                      ? `${saved.balance_variance_kg > 0 ? '+' : ''}${saved.balance_variance_kg.toFixed(0)} kg (${saved.balance_variance_pct?.toFixed(1)}%)`
                      : '—'}
                  </Text>
                </span>
              )}
              <Button size="small" onClick={() => {
                const prefill: Record<string, number | null> = {}
                for (const l of saved!.lines ?? []) prefill[l.category_id] = l.kg
                setPerCat(prefill)
                setNotes(saved!.notes ?? '')
                setContamination(saved!.contamination_kg ?? null)
                setRejection(saved!.rejection_kg ?? null)
                setEditing(true)
              }}>Correct</Button>
            </>
          )}
          {!isSaved && (
            <>
              {editing ? renderEditor() : (
                <Button type="primary" size="small" onClick={() => setEditing(true)}>
                  Enter actual swept kg
                </Button>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                Weigh each waste category at shift end. Threshold ±{threshold_pct}%.
              </Text>
            </>
          )}
          {isSaved && editing && renderEditor()}
        </Space>
      }
    />
  )
}

// ─── Sprint 0143 — Carding Can Summary banner ─────────────────────────
// Section-level can rollup: produced, transferred to Drawing, on hold,
// rejected, empties available. All auto-derived from the cans table +
// production logs — operator never enters can data.
function CardingCanSummaryBanner({
  plantId, logDate, shiftId,
}: {
  plantId: string
  logDate: string
  shiftId: string
}) {
  const sumQ = useQuery({
    enabled: !!plantId && !!shiftId,
    queryKey: ['carding-can-summary', plantId, logDate, shiftId],
    queryFn: () => cardingApi.canSummary({
      plant_id: plantId, log_date: logDate, shift_id: shiftId,
    }),
    refetchInterval: 30_000,
  })

  if (!sumQ.data) return null
  const s = sumQ.data.section
  const targetPct = s.target_count > 0
    ? Math.round((s.produced_count / s.target_count) * 100)
    : null
  const color =
    targetPct === null ? '#1677ff'
    : targetPct >= 95 ? '#52c41a'
    : targetPct >= 70 ? '#1677ff'
    : targetPct >= 40 ? '#fa8c16'
    : '#cf1322'

  const hasAlerts = s.qc_held_count > 0 || s.qc_rejected_count > 0
    || s.empties_available_count === 0

  return (
    <Alert
      type={hasAlerts ? 'warning' : 'info'}
      showIcon
      style={{ marginBottom: 12 }}
      message={
        <Space wrap size="middle">
          <span>
            <Text strong>Cans today:</Text>{' '}
            <Text strong style={{ color, fontSize: 16 }}>
              {s.produced_count}
            </Text>
            {s.target_count > 0 && (
              <Text type="secondary">
                {' / target '}{s.target_count}
                {targetPct !== null && ` (${targetPct}%)`}
              </Text>
            )}
            {' '}
            <Text type="secondary">
              ({s.produced_kg.toFixed(0)} kg)
            </Text>
          </span>

          {s.transferred_to_drawing_count > 0 && (
            <Tag color="blue">
              Transferred to Drawing: <b>{s.transferred_to_drawing_count}</b>
            </Tag>
          )}
          {s.consumed_count > 0 && (
            <Tag color="purple">
              Consumed: <b>{s.consumed_count}</b>
            </Tag>
          )}
          {s.in_chute_count > 0 && (
            <Tag color="cyan">
              In chute: <b>{s.in_chute_count}</b>
            </Tag>
          )}
          {s.qc_pending_count > 0 && (
            <Tag>
              QC pending: <b>{s.qc_pending_count}</b>
            </Tag>
          )}
          {s.qc_held_count > 0 && (
            <Tag color="orange">
              QC held: <b>{s.qc_held_count}</b>
            </Tag>
          )}
          {s.qc_rejected_count > 0 && (
            <Tag color="red">
              Rejected: <b>{s.qc_rejected_count}</b>
            </Tag>
          )}
          {s.empties_available_count > 0 ? (
            <Tag color="green">
              Empty cans available: <b>{s.empties_available_count}</b>
            </Tag>
          ) : (
            <Tag color="red">
              ⚠ No empty cans tracked
            </Tag>
          )}
          {s.partial_can_kg > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Plus {s.partial_can_kg.toFixed(1)} kg currently filling
            </Text>
          )}
        </Space>
      }
    />
  )
}

// ─── Sprint 0143 — Comber live reconciliation alert ──────────────────────
// Per addendum Section 4: input_kg ≈ output_kg + noil_kg + waste_kg.
// When the operator types output/noil/waste, this watches the form's
// input_received_kg + actual_output_kg + rejection_kg (noil) + waste_kg
// and shows a live alert when the balance exceeds the per-machine
// reconciliation tolerance. Mirrors comber_reconciliation() formula.
function ComberReconciliationAlert({
  form, tolerancePct, stdNoilPct,
}: {
  form: any
  tolerancePct: number
  stdNoilPct: number
}) {
  const inputKg = Form.useWatch('input_received_kg', form) as number | undefined
  const outputKg = Form.useWatch('actual_output_kg', form) as number | undefined
  const noilKg = Form.useWatch('rejection_kg', form) as number | undefined
  const wasteKg = Form.useWatch('waste_kg', form) as number | undefined

  const inK = Number(inputKg ?? 0)
  const outK = Number(outputKg ?? 0)
  const noK = Number(noilKg ?? 0)
  const waK = Number(wasteKg ?? 0)
  const accounted = outK + noK + waK
  const unaccounted = inK - accounted
  const unaccountedPct = inK > 0 ? Math.abs(unaccounted) / inK * 100 : null

  if (inK <= 0 || outK <= 0) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          <span>
            <Text strong>Comber reconciliation:</Text>{' '}
            <Text type="secondary">
              fill Input + Output + Noil to verify input matches output + noil + waste.
              Standard noil <b>{stdNoilPct}%</b>, tolerance <b>±{tolerancePct}%</b>.
            </Text>
          </span>
        }
      />
    )
  }

  const breach = unaccountedPct !== null && unaccountedPct > tolerancePct
  const noilActualPct = inK > 0 ? noK / inK * 100 : null

  return (
    <Alert
      type={breach ? 'error' : 'success'}
      showIcon
      style={{ marginBottom: 12 }}
      message={
        <span>
          <Text strong>Comber reconciliation:</Text>{' '}
          Input <b>{inK.toFixed(1)} kg</b>{' '}
          = Output <b>{outK.toFixed(1)}</b>
          {' '}+ Noil <b>{noK.toFixed(1)}</b>
          {' '}+ Waste <b>{waK.toFixed(1)}</b>
          {' = '}<b>{accounted.toFixed(1)} kg</b>.
          {' '}
          {unaccountedPct !== null && (
            <Tag color={breach ? 'red' : 'green'} style={{ marginLeft: 4 }}>
              {unaccounted >= 0 ? '+' : ''}{unaccounted.toFixed(1)} kg
              {' ('}{unaccountedPct.toFixed(2)}%{' vs '}{tolerancePct}%{')'}
            </Tag>
          )}
          {noilActualPct !== null && (
            <Tag color={Math.abs(noilActualPct - stdNoilPct) > 3 ? 'orange' : 'default'} style={{ marginLeft: 4 }}>
              Actual noil {noilActualPct.toFixed(2)}% (std {stdNoilPct}%)
            </Tag>
          )}
          {breach && (
            <div style={{ marginTop: 6 }}>
              <Text type="danger">
                Exceeds tolerance. Re-check weighing or add an Exception Reason.
                System flags this for GM cross-check.
              </Text>
            </div>
          )}
        </span>
      }
    />
  )
}
